import { Button, Image, Space } from "antd";
import { useState } from "react";
import { BsDownload, BsZoomIn, BsZoomOut } from "react-icons/bs";
import { useTranslation } from "react-i18next";
import locale from "../../../constants/locale";

const imgStyle = {
  height: "100%",
  width: "100%",
  bottom: 0,
  left: 0,
  position: "absolute",
  right: 0,
  top: 0,
  maxHeight: "100%",
  objectFit: "cover",
  objectPosition: "center",
};

export default function MessageImage({
  src,
  meta,
  resolveSrc,
}: {
  src: string;
  meta?: any;
  resolveSrc?: () => Promise<string>;
}) {
  const { t } = useTranslation();
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState("");

  const onDownload = async () => {
    if (!src) return;
    if (src.startsWith("https://api.twilio.com/")) {
      window.open(src, "_blank");
      return;
    }
    setDownloading(true);
    setError("");
    try {
      const response = await fetch(resolveSrc ? await resolveSrc() : src);
      if (!response.ok) throw new Error(`Image download failed (HTTP ${response.status})`);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "image.png";
      document.body.appendChild(link);
      link.click();
      URL.revokeObjectURL(url);
      link.remove();
    } catch (error) { setError(error instanceof Error ? error.message : t(locale.resourceNotFound)); } finally {
      setDownloading(false);
    }
  };
  return (
    <>
      <Image
        wrapperStyle={{
          display: "block",
          paddingTop: "100%",
          position: "relative",
        }}
        className="has-rounded-corners"
        style={imgStyle as any}
        src={src}
        referrerPolicy="no-referrer"
        preview={{
          onVisibleChange: (visible) => {
            if (visible && resolveSrc) void resolveSrc().then(() => setError(""))
              .catch(error => setError(error instanceof Error ? error.message : t(locale.resourceNotFound)));
          },
          toolbarRender: (
            _,
            { transform: { scale }, actions: { onZoomOut, onZoomIn } }
          ) => (
            <Space
              size={12}
              className="has-background-black-ter has-rounded-corners p-2"
            >
              <Button
                ghost
                type="link"
                icon={<BsDownload color="white" size={26} />}
                onClick={onDownload}
                loading={downloading}
              />
              <Button
                ghost
                type="link"
                disabled={scale === 1}
                icon={<BsZoomOut color="white" size={26} />}
                onClick={onZoomOut}
              />
              <Button
                ghost
                type="link"
                disabled={scale === 50}
                icon={<BsZoomIn color="white" size={26} />}
                onClick={onZoomIn}
              />
            </Space>
          ),
        }}
      />
      {meta?.caption && <p>{meta.caption}</p>}
      {error && <small role="alert">{error}</small>}
    </>
  );
}
